import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CaretLeft, DownloadSimple, Trash, MapPin, WifiSlash, HardDrives,
  Check, Spinner, MapTrifold, Plus, ArrowCounterClockwise
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { MapContainer, TileLayer, Rectangle, useMapEvents, useMap } from "react-leaflet";
import { openDB } from "idb";

const TILE_DB_NAME = "NatureJournalMapTiles";
const TILE_DB_VERSION = 1;
const TILE_STORE = "tiles";
const REGION_STORE = "regions";

const initTileDB = async () => {
  return openDB(TILE_DB_NAME, TILE_DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(TILE_STORE)) {
        db.createObjectStore(TILE_STORE, { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains(REGION_STORE)) {
        db.createObjectStore(REGION_STORE, { keyPath: "id" });
      }
    },
  });
};

const lat2tile = (lat, zoom) => {
  return Math.floor((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, zoom));
};

const lon2tile = (lon, zoom) => {
  return Math.floor((lon + 180) / 360 * Math.pow(2, zoom));
};

const getTilesForBounds = (bounds, zoom) => {
  const tiles = [];
  const minX = lon2tile(bounds.west, zoom);
  const maxX = lon2tile(bounds.east, zoom);
  const minY = lat2tile(bounds.north, zoom);
  const maxY = lat2tile(bounds.south, zoom);
  for (let x = minX; x <= maxX; x++) {
    for (let y = minY; y <= maxY; y++) {
      tiles.push({ x, y, z: zoom });
    }
  }
  return tiles;
};

const BoundsSelector = ({ onBoundsSelected, selecting }) => {
  const [start, setStart] = useState(null);
  const [current, setCurrent] = useState(null);

  useMapEvents({
    mousedown(e) {
      if (selecting) {
        setStart(e.latlng);
        setCurrent(e.latlng);
      }
    },
    mousemove(e) {
      if (selecting && start) {
        setCurrent(e.latlng);
      }
    },
    mouseup(e) {
      if (selecting && start) {
        const bounds = {
          north: Math.max(start.lat, e.latlng.lat),
          south: Math.min(start.lat, e.latlng.lat),
          east: Math.max(start.lng, e.latlng.lng),
          west: Math.min(start.lng, e.latlng.lng)
        };
        onBoundsSelected(bounds);
        setStart(null);
        setCurrent(null);
      }
    }
  });

  if (!start || !current) return null;

  return (
    <Rectangle
      bounds={[[start.lat, start.lng], [current.lat, current.lng]]}
      pathOptions={{ color: "#C25A3C", weight: 2, fillColor: "#C25A3C", fillOpacity: 0.15, dashArray: "5,5" }}
    />
  );
};

const OfflineMapPage = () => {
  const navigate = useNavigate();
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(false);
  const [selectedBounds, setSelectedBounds] = useState(null);
  const [regionName, setRegionName] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalTiles, setTotalTiles] = useState(0);
  const [downloadedTiles, setDownloadedTiles] = useState(0);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [storageUsed, setStorageUsed] = useState(0);
  const cancelRef = useRef(false);

  const loadRegions = useCallback(async () => {
    try {
      const db = await initTileDB();
      const saved = await db.getAll(REGION_STORE);
      setRegions(saved);
      const allTiles = await db.count(TILE_STORE);
      setStorageUsed(allTiles * 15);
    } catch (error) {
      console.error("Failed to load regions:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRegions(); }, [loadRegions]);

  const handleBoundsSelected = (bounds) => {
    setSelectedBounds(bounds);
    setSelecting(false);
    setShowSaveDialog(true);

    let total = 0;
    for (let z = 10; z <= 15; z++) {
      total += getTilesForBounds(bounds, z).length;
    }
    setTotalTiles(total);
  };

  const handleDownload = async () => {
    if (!regionName.trim()) { toast.error("Please enter a region name"); return; }
    if (!selectedBounds) return;

    setDownloading(true);
    setProgress(0);
    setDownloadedTiles(0);
    cancelRef.current = false;

    const db = await initTileDB();
    let downloaded = 0;
    let failed = 0;
    const allTiles = [];

    for (let z = 10; z <= 15; z++) {
      allTiles.push(...getTilesForBounds(selectedBounds, z));
    }

    const total = allTiles.length;
    setTotalTiles(total);

    const batchSize = 6;
    for (let i = 0; i < allTiles.length; i += batchSize) {
      if (cancelRef.current) break;

      const batch = allTiles.slice(i, i + batchSize);
      const results = await Promise.allSettled(
        batch.map(async (tile) => {
          const url = `https://a.tile.openstreetmap.org/${tile.z}/${tile.x}/${tile.y}.png`;
          const key = `${tile.z}/${tile.x}/${tile.y}`;
          try {
            const existing = await db.get(TILE_STORE, key);
            if (existing) return true;

            const response = await fetch(url);
            if (!response.ok) throw new Error("Fetch failed");
            const blob = await response.blob();
            const arrayBuffer = await blob.arrayBuffer();
            await db.put(TILE_STORE, { key, data: arrayBuffer, timestamp: Date.now() });
            return true;
          } catch {
            return false;
          }
        })
      );

      results.forEach(r => {
        if (r.status === "fulfilled" && r.value) downloaded++;
        else failed++;
      });

      setDownloadedTiles(downloaded);
      setProgress(Math.round(((downloaded + failed) / total) * 100));
    }

    if (!cancelRef.current) {
      const region = {
        id: `region-${Date.now()}`,
        name: regionName.trim(),
        bounds: selectedBounds,
        tileCount: downloaded,
        zoomLevels: "10-15",
        downloadedAt: new Date().toISOString(),
        sizeEstimateKB: downloaded * 15
      };
      await db.put(REGION_STORE, region);

      toast.success(`Downloaded ${downloaded} tiles for "${regionName}"`);
      if (failed > 0) toast.info(`${failed} tiles could not be cached`);
    } else {
      toast.info("Download cancelled");
    }

    setDownloading(false);
    setShowSaveDialog(false);
    setSelectedBounds(null);
    setRegionName("");
    loadRegions();
  };

  const handleDeleteRegion = async (regionId) => {
    try {
      const db = await initTileDB();
      const region = await db.get(REGION_STORE, regionId);
      if (region) {
        for (let z = 10; z <= 15; z++) {
          const tiles = getTilesForBounds(region.bounds, z);
          for (const tile of tiles) {
            await db.delete(TILE_STORE, `${tile.z}/${tile.x}/${tile.y}`);
          }
        }
      }
      await db.delete(REGION_STORE, regionId);
      toast.success("Region deleted");
      loadRegions();
    } catch (error) {
      toast.error("Failed to delete region");
    }
  };

  const formatSize = (kb) => {
    if (kb < 1024) return `${kb} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="max-w-7xl mx-auto page-content" data-testid="offline-map-page">
      <div className="flex flex-col gap-2 mb-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate("/map")} className="text-[#55624F] shrink-0" data-testid="back-to-map">
            <CaretLeft size={20} />
          </Button>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white">Offline Maps</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 pl-1">
          <Badge className="bg-[#55624F] text-white text-xs">
            <WifiSlash size={12} className="mr-1" />Offline Ready
          </Badge>
          <div className="flex items-center gap-1 text-xs text-white/70">
            <HardDrives size={14} />
            <span>{formatSize(storageUsed)} cached</span>
          </div>
        </div>
        <p className="text-xs sm:text-base text-white/70 pl-1">Download map areas for offline use</p>
      </div>

      {/* Saved Regions */}
      {regions.length > 0 && (
        <div className="mb-8">
          <h2 className="font-heading text-lg font-semibold text-white mb-3">Cached Regions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {regions.map(region => (
              <Card key={region.id} className="bg-white border-[#D2C2B3]" data-testid={`region-${region.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-[#2E332B] flex items-center gap-2">
                        <MapTrifold size={18} weight="duotone" className="text-[#55624F]" />
                        {region.name}
                      </h3>
                      <div className="flex flex-wrap gap-2 mt-2 text-xs text-white/70">
                        <span>{region.tileCount} tiles</span>
                        <span>{formatSize(region.sizeEstimateKB)}</span>
                        <span>Zoom {region.zoomLevels}</span>
                      </div>
                      <p className="text-xs text-[#7C8068] mt-1">
                        Cached {new Date(region.downloadedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteRegion(region.id)} className="text-[#C25A3C] hover:bg-[#C25A3C]/10" data-testid={`delete-region-${region.id}`}>
                      <Trash size={16} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Map for selecting area */}
      <Card className="bg-white border-[#D2C2B3] mb-4">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="font-heading text-lg sm:text-xl text-[#2E332B]">Select Area to Cache</CardTitle>
            <Button
              onClick={() => setSelecting(!selecting)}
              className={selecting ? "bg-[#C25A3C] hover:bg-[#A84B32] text-white" : "bg-[#55624F] hover:bg-[#424D3D] text-white"}
              data-testid="select-area-btn"
            >
              {selecting ? (
                <><ArrowCounterClockwise size={18} className="mr-2" />Cancel Selection</>
              ) : (
                <><Plus size={18} className="mr-2" />Select Area</>
              )}
            </Button>
          </div>
          {selecting && (
            <p className="text-sm text-[#C25A3C] mt-2 font-medium">
              Click and drag on the map to draw a rectangle around the area you want to cache
            </p>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          <div className={`h-[400px] rounded-lg overflow-hidden border border-[#D2C2B3] ${selecting ? "cursor-crosshair" : ""}`}>
            <MapContainer center={[40.7128, -74.0060]} zoom={6} className="h-full w-full">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <BoundsSelector onBoundsSelected={handleBoundsSelected} selecting={selecting} />
              {selectedBounds && (
                <Rectangle
                  bounds={[[selectedBounds.north, selectedBounds.west], [selectedBounds.south, selectedBounds.east]]}
                  pathOptions={{ color: "#55624F", weight: 2, fillColor: "#55624F", fillOpacity: 0.1 }}
                />
              )}
              {regions.map(region => (
                <Rectangle
                  key={region.id}
                  bounds={[[region.bounds.north, region.bounds.west], [region.bounds.south, region.bounds.east]]}
                  pathOptions={{ color: "#55624F", weight: 1, fillColor: "#55624F", fillOpacity: 0.05, dashArray: "3,3" }}
                />
              ))}
            </MapContainer>
          </div>
          <p className="text-xs text-[#7C8068] mt-2 text-center">
            Green dashed rectangles show previously cached regions. Tiles are cached at zoom levels 10-15.
          </p>
        </CardContent>
      </Card>

      {/* Save Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={(open) => { if (!downloading) { setShowSaveDialog(open); if (!open) setSelectedBounds(null); } }}>
        <DialogContent className="bg-white border-[#D2C2B3] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <DownloadSimple size={24} weight="duotone" className="text-[#55624F]" />
              Cache Map Region
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div>
              <label className="block text-sm font-medium text-[#55624F] mb-2">Region Name</label>
              <Input
                value={regionName}
                onChange={(e) => setRegionName(e.target.value)}
                placeholder="e.g., Appalachian Trail Section"
                className="bg-[#EAE7DE] border-[#D2C2B3]"
                disabled={downloading}
                data-testid="region-name-input"
              />
            </div>

            <div className="bg-[#EAE7DE] rounded-lg p-3 text-sm">
              <div className="flex justify-between text-[#7C8068] mb-1">
                <span>Tiles to download:</span>
                <span className="font-medium text-[#2E332B]">{totalTiles}</span>
              </div>
              <div className="flex justify-between text-[#7C8068] mb-1">
                <span>Estimated size:</span>
                <span className="font-medium text-[#2E332B]">{formatSize(totalTiles * 15)}</span>
              </div>
              <div className="flex justify-between text-[#7C8068]">
                <span>Zoom levels:</span>
                <span className="font-medium text-[#2E332B]">10 - 15</span>
              </div>
            </div>

            {downloading && (
              <div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-[#55624F]">Downloading tiles...</span>
                  <span className="text-[#7C8068]">{downloadedTiles} / {totalTiles}</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            )}

            <div className="flex gap-3">
              {downloading ? (
                <Button onClick={() => { cancelRef.current = true; }} variant="outline" className="flex-1 border-[#C25A3C] text-[#C25A3C]" data-testid="cancel-download">
                  Cancel Download
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => { setShowSaveDialog(false); setSelectedBounds(null); }} className="flex-1 border-[#D2C2B3]">
                    Cancel
                  </Button>
                  <Button onClick={handleDownload} className="flex-1 bg-[#55624F] hover:bg-[#424D3D] text-white" data-testid="start-download">
                    <DownloadSimple size={18} className="mr-2" />Download Tiles
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OfflineMapPage;
